import { useMatrixSettingsContext } from '@app/components/products/ProductMatrix/context';
import { Spinner } from '@app/components/various/Spinner';
import { cn } from '@app/utils/cn';
import { useMatrixLayout } from '@app/utils/hooks/matrix/useMatrixLayout';
import React from 'react';

interface Props {
  className?: string;
  rowSpan?: number;
  dataTestId?: string;
  color?: string;
  isDisabled?: boolean;
  isSelected?: boolean;
  isFocusTarget?: boolean;
  innerStyles?: React.CSSProperties;
  innerClassName?: string;
  isLoading?: boolean;
}

export const RegularBodyCell = React.forwardRef<HTMLDivElement, React.WithChildren<Props>>((props, ref) => {
  const {
    innerStyles,
    innerClassName,
    children,
    className,
    rowSpan,
    isSelected,
    isDisabled = false,
    dataTestId,
    color,
    isFocusTarget,
    isLoading = false,
  } = props;

  const { hasMultipleColumns } = useMatrixLayout();
  const { isTablet, isMobile, isOrderEditable, isReadOnly } = useMatrixSettingsContext();

  return (
    // this element doesn't need key event listener (rule jsx-a11y/click-events-have-key-events is disabled)
    // because keyboard navigation is already handled by MatrixNavigationContext
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <td
      data-testid={dataTestId}
      rowSpan={rowSpan}
      className={cn(
        'relative box-content h-[--matrixCellHeight] min-w-[--matrixCellWidth] select-none border-2 border-transparent bg-clip-padding p-0 body-2',
        className,
        {
          'min-w-64': isTablet && !hasMultipleColumns,
          'w-full max-w-64': isMobile && !hasMultipleColumns,
        },
      )}
      color={color}
    >
      <div
        style={innerStyles}
        ref={ref}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        data-focus-target={isFocusTarget}
        className={cn(
          'relative flex h-full w-full items-center justify-center rounded-regular border border-transparent bg-neutral-96 transition-colors focus:outline-none',
          {
            'bg-transparent': isDisabled && isOrderEditable,
            'bg-white': isReadOnly,
            'border-info-62': isSelected,
            'hover:border-neutral-90': !isSelected,
          },
          innerClassName,
        )}
      >
        {children}
        {isLoading && <Spinner className="absolute right-0 top-0 m-0.5 size-3" />}
      </div>
    </td>
  );
});
